// Patička okenního frameworku (Windows-style taskbar).
//   - vlevo skupina „Plocha" = naskládaný docked stack (jen počet, bez záložek)
//   - pak záložky floating skupin (skupina = 1 záložka s počítadlem)
//   - minimalizovaná okna = samostatné čárkované záložky

import { useMemo, type ReactNode } from 'react';

import {
  useNavigationContext,
  useNavigationStore,
} from '@/shared/navigation/react/useNavigationContext';
import { resolveAgendaIcon } from '@/shared/navigation/shell';
import { Icon } from '@/shared/ui/icon';

import { deriveDesktopProjection } from '../core/deriveDesktopProjection';
import { useWindowManagerStore } from '../core/windowManagerStore';
import { useNativeWindowTitleStore } from '../core/nativeWindowTitleStore';
import { useLegacyWindowChromeStore } from '../legacy/legacyWindowChromeStore';

import { useWindowingActions } from './useWindowingActions';

function okenWord(n: number): string {
  if (n === 1) return 'okno';
  if (n >= 2 && n <= 4) return 'okna';
  return 'oken';
}

/** Ikona agendy v záložce taskbaru — stejný resolver jako typový chip v hlavičce okna
 *  (fallback box). Sedí do 14px boxu `.pwm-task-ico`. */
function TaskbarIcon({ icon }: { icon: string | undefined }) {
  const IconComponent = resolveAgendaIcon(icon);
  return <IconComponent className="h-2.5 w-2.5" aria-hidden />;
}

export interface PhoenixTaskbarProps {
  readonly buildLabel?: string;
  /** Slot vedle „Plochy" (např. tlačítko Historie). */
  readonly historySlot?: ReactNode;
  /** Info slot vpravo v patičce (např. ENV/DB badge). */
  readonly infoSlot?: ReactNode;
}

export function PhoenixTaskbar({ buildLabel, historySlot, infoSlot }: PhoenixTaskbarProps) {
  const { kernel } = useNavigationContext();
  const navigation = useNavigationStore((s) => s.navigation);
  const windows = useWindowManagerStore((s) => s.windows);
  const activeLayerId = useWindowManagerStore((s) => s.activeLayerId);
  const chromeByLayer = useLegacyWindowChromeStore((s) => s.byLayer);
  const titleByLayer = useNativeWindowTitleStore((s) => s.byLayer);
  const actions = useWindowingActions();

  const projection = useMemo(
    () =>
      deriveDesktopProjection({
        state: navigation,
        windows,
        activeLayerId,
        registry: kernel.getRegistry(),
        chromeByLayer,
        titleByLayer,
      }),
    [navigation, windows, activeLayerId, kernel, chromeByLayer, titleByLayer]
  );

  const minimized = projection.allWindows.filter((w) => w.mode === 'minimized');

  // Floating skupiny: zobraz jen ty s ne-minimalizovaným oknem; top = nejvyšší z.
  const groupTabs = projection.floatingGroups
    .map((g) => {
      const visible = g.windows.filter((w) => w.mode !== 'minimized');
      if (visible.length === 0) return null;
      const base = g.windows.find((w) => w.isBase) ?? visible[0];
      const top = visible.reduce((a, b) => (b.withinIndex > a.withinIndex ? b : a));
      const active = visible.some((w) => w.isGlobalActive);
      return { stackId: g.stackId as string, base, top, count: visible.length, active };
    })
    .filter((t): t is NonNullable<typeof t> => t !== null);

  const dockedCount = projection.plocha.length;
  const plochaTop = projection.plocha[projection.plocha.length - 1];

  return (
    <div className="pwm-taskbar">
      {historySlot}
      <div
        className="pwm-taskbar-stack"
        onClick={() => plochaTop && actions.focus(plochaTop.layerId)}
        title="Pracovní plocha — naskládaná okna"
      >
        <Icon name="layers" size={14} className="text-phx-text-2" />
        <b>Plocha</b>
        <span>
          {dockedCount} {okenWord(dockedCount)}
        </span>
      </div>
      <span className="pwm-taskbar-divider" />
      <div className="pwm-taskbar-items">
        {groupTabs.length === 0 && minimized.length === 0 && (
          <span className="text-phx-xs text-phx-text-faint pl-phx-1">
            Osamostatněná okna se objeví zde
          </span>
        )}

        {groupTabs.map((t) => (
          <div
            key={t.stackId}
            className="pwm-task"
            {...(t.active ? { 'data-active': true } : {})}
            onClick={() => actions.focus(t.top.layerId)}
            title={t.base.title}
          >
            <span
              className="pwm-task-ico"
              data-kind={t.base.kind === 'native' ? 'native' : 'legacy'}
            >
              <TaskbarIcon icon={t.base.icon} />
            </span>
            <span className="pwm-task-label">{t.base.title}</span>
            {t.count > 1 && (
              <span className="pwm-task-count" title={`${t.count} ${okenWord(t.count)} ve stacku`}>
                {t.count}
              </span>
            )}
            <button
              type="button"
              className="pwm-task-close"
              title="Zavřít skupinu"
              onClick={(e) => {
                e.stopPropagation();
                actions.closeGroup(t.stackId);
              }}
            >
              <Icon name="x" size={11} />
            </button>
          </div>
        ))}

        {minimized.map((w) => (
          <div
            key={w.layerId}
            className="pwm-task"
            data-min
            onClick={() => actions.taskClick(w.layerId)}
            title={w.title}
          >
            <span className="pwm-task-ico" data-kind={w.kind === 'native' ? 'native' : 'legacy'}>
              <TaskbarIcon icon={w.icon} />
            </span>
            <span className="pwm-task-label">{w.title}</span>
            <button
              type="button"
              className="pwm-task-close"
              onClick={(e) => {
                e.stopPropagation();
                actions.close(w.layerId);
              }}
            >
              <Icon name="x" size={11} />
            </button>
          </div>
        ))}
      </div>
      <span className="pwm-taskbar-right">
        {infoSlot}
        {buildLabel && <span className="font-mono">{buildLabel}</span>}
      </span>
    </div>
  );
}
